import classnames from 'classnames';
import React from 'react';
import s from './InputField.scss';
import { Box, FloatingHelper } from 'wix-style-react';
import {
  CircleSmallFilledSmall,
  StatusCompleteSmall,
} from '@wix/wix-ui-icons-common';
import {
  PasswordStrengthExtractor,
  PasswordStrengthHint,
} from '../../utils/passwordStrength';

import type { PasswordStrengthClassification } from '../../utils/passwordStrength';
import { useTranslation } from 'react-i18next';
import { ThemedText } from '../ThemedComponents';

const passwordStrength = new PasswordStrengthExtractor();
interface WithPasswordStrengthProps {
  showStrength: boolean;
  password: string;
  isMobile?: boolean;
}

export function withPasswordStrength<T extends any>(
  WrappedComponent: React.ComponentType<T>
) {
  // Try to create a nice displayName for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const InputWithPasswordStrength = (
    props: T & WithPasswordStrengthProps & any
  ) => {
    const { t } = useTranslation();
    const [visible, setVisible] = React.useState(false);
    const result = passwordStrength.getPasswordStrength(props.password);
    const classification = fromRawClassification({
      password: props.password,
      classification: result.classification,
    });
    const strengthSuffix = (
      <Box
        key="password-strength"
        verticalAlign="middle"
        marginRight={props.suffix ? 1 : 0}
        marginBottom="2px"
      >
        <ThemedText size="tiny" secondary>
          {t(
            getPasswordStrengthTranslationKey({
              password: props.password,
              classification: result.classification,
            })
          )}
        </ThemedText>
      </Box>
    );
    const suffix = [
      ...(props.showStrength && !visible ? [strengthSuffix] : []),
      ...(props.suffix ? props.suffix : []),
    ];

    const onFocus = React.useCallback(
      (e: React.FocusEventHandler<HTMLInputElement>) => {
        setVisible(true);
        return props?.onFocus?.(e);
      },
      [props?.onFocus]
    );

    const onBlur = React.useCallback(
      (e: React.FocusEventHandler<HTMLInputElement>) => {
        setVisible(false);
        return props?.onBlur?.(e);
      },
      props?.onBlur
    );

    // props comes afterwards so the can override the default ones.
    const component = (
      <WrappedComponent
        {...(props as T & {})}
        suffix={suffix}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
    return props.showStrength ? (
      props.isMobile ? (
        <Box direction="vertical">
          {component}
          {visible && (
            <Box>
              <PasswordStrengthBody
                finalScore={result.finalScore as number}
                classification={classification}
                hints={result.hints}
                password={props.password}
                isMobile={props.isMobile}
              />
            </Box>
          )}
        </Box>
      ) : (
        <FloatingHelper
          width="270px"
          opened={visible}
          appearance="light"
          target={component}
          placement="left"
          content={
            <FloatingHelper.Content
              body={
                <PasswordStrengthBody
                  finalScore={result.finalScore as number}
                  classification={classification}
                  hints={result.hints}
                  password={props.password}
                />
              }
            />
          }
        />
      )
    ) : (
      component
    );
  };

  InputWithPasswordStrength.displayName = `withPasswordStrength(${displayName})`;

  return InputWithPasswordStrength;
}

const PasswordStrengthBody: React.FC<{
  finalScore: number;
  classification: PasswordStrengthClassification | '';
  hints: PasswordStrengthHint[];
  password: string;
  isMobile?: boolean;
}> = ({ finalScore, classification, hints, password, isMobile }) => {
  const { t } = useTranslation();
  // `hints` includes the password properties the current password miss so in
  // order to figure what the password includes we check what is missing
  const includeLetters = !hints.includes('letters');
  const includeSymbols = !hints.includes('symbol');
  const includeMoreCharacters = !hints.includes('more_characters');
  return (
    <Box
      direction="vertical"
      width="100%"
      gap={isMobile ? 2 : undefined}
      textAlign="left"
    >
      <Box
        direction={isMobile ? 'horizontal' : 'vertical'}
        align="space-between"
      >
        <ThemedText
          size="small"
          secondary
          aria-live="polite"
          aria-atomic="true"
          weight={isMobile ? 'bold' : undefined}
        >
          {t(
            getTitleTranslationKey({
              password,
              classification: classification as PasswordStrengthClassification,
            })
          )}
        </ThemedText>
        {(!isMobile ||
          password?.length >= PasswordStrengthExtractor.MINIMAL_LENGTH) && (
          <Box
            className={classnames([
              s.strengthIndicatorContainer,
              { [s[`level-${classification}`]]: classification?.length > 0 },
              { [s.mobilePasswordStrength]: isMobile },
            ])}
            direction="horizontal"
            paddingTop={1}
            paddingBottom={1}
          >
            <Box
              className={classnames([s.bar, { [s.isOn]: finalScore >= 0 }])}
            />
            <Box
              className={classnames([s.bar, { [s.isOn]: finalScore > 1 }])}
            />
            <Box
              className={classnames([s.bar, { [s.isOn]: finalScore > 2 }])}
            />
            <Box
              className={classnames([s.bar, { [s.isOn]: finalScore > 4 }])}
            />
          </Box>
        )}
      </Box>
      <Box
        aria-live="polite"
        aria-atomic="true"
        direction="vertical"
        gap={isMobile ? 1 : undefined}
      >
        <ThemedText
          size="small"
          secondary
          textAlign={isMobile ? 'left' : 'center'}
        >
          {t('signup.password_tooltip_strength_details_title')}
        </ThemedText>
        <Hint predicate={includeLetters}>
          {t('signup.password_tooltip_strength_details_letters')}
        </Hint>
        <Hint predicate={includeSymbols}>
          {t('signup.password_tooltip_strength_details_symbol')}
        </Hint>
        <Hint predicate={includeMoreCharacters}>
          {t('signup.password_tooltip_strength_details_more_characters')}
        </Hint>
      </Box>
    </Box>
  );
};

const Hint: React.FC<{ predicate: boolean }> = ({ predicate, children }) => {
  return (
    <Box>
      <Box color="B05" marginRight={1}>
        {predicate ? <StatusCompleteSmall /> : <CircleSmallFilledSmall />}
      </Box>
      <ThemedText size="small" secondary>
        {children}
      </ThemedText>
    </Box>
  );
};

const fromRawClassification = ({
  password,
  classification,
}: {
  password: string;
  classification?: PasswordStrengthClassification;
}) => {
  return classification &&
    password &&
    password.length >= PasswordStrengthExtractor.MINIMAL_LENGTH
    ? classification
    : '';
};

const getTitleTranslationKey = ({
  password,
  classification,
}: {
  password: string;
  classification?: PasswordStrengthClassification;
}) => {
  return classification &&
    password?.length >= PasswordStrengthExtractor.MINIMAL_LENGTH
    ? `signup.password_tooltip_strength_bar_level_${classification}`
    : 'signup.password_tooltip_strength_bar_too_short';
};

const getPasswordStrengthTranslationKey = ({
  password,
  classification,
}: {
  password: string;
  classification?: PasswordStrengthClassification;
}) => {
  return classification && password
    ? `signup.password_tooltip_strength_level_${classification}`
    : '';
};
